/*
 * Now-playing card skins (Skins, mockup 35, ENG-735). A skin of type
 * `now_playing_card` decorates the now-playing card. The server hands its
 * render data over as stored (`SkinsSkin.assets`, backend ENG-736), in the
 * shape the backend pins in its tests:
 *
 *   { imageUrl?: string,        // a stored media path or an absolute URL
 *     accentColor?: '#RRGGBB',  // the card's frame and wash
 *     animation?: { kind: 'spin', durationMs } }  // the record's spin speed
 *
 * Every field is optional and anything else is ignored, so a skin with empty
 * or unknown assets still draws as a frame in its rarity colour.
 *
 * "See what they see" previews another listener's card skin on this viewer's
 * card only. The preview ends on "Back to mine" or when the playing song
 * changes (Tim's decision on the ticket's open question); `startPreview` and
 * `previewForSong` hold those rules so they can be tested without a page.
 */
import { resolveMediaUrl } from '../hooks/useStreamBootstrap.js';

export const NOW_PLAYING_CARD_SKIN = 'now_playing_card';

const RARITIES = ['common', 'rare', 'epic', 'legendary'];
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
// A spin faster than this strobes and slower than this looks stopped.
const MIN_SPIN_MS = 400;
const MAX_SPIN_MS = 20000;

/**
 * How a now-playing card skin draws, from its catalog entry
 * (`{ id, name, type, rarity, assets }`); null for any other skin type.
 */
export const cardSkinLook = (skin) => {
  if (!skin || typeof skin !== 'object' || skin.type !== NOW_PLAYING_CARD_SKIN) return null;
  const assets = skin.assets && typeof skin.assets === 'object' ? skin.assets : {};
  const accent = typeof assets.accentColor === 'string' ? assets.accentColor.trim() : '';
  const image = typeof assets.imageUrl === 'string' ? assets.imageUrl.trim() : '';
  const animation = assets.animation && typeof assets.animation === 'object' ? assets.animation : null;
  const spin = animation?.kind === 'spin' ? Number(animation.durationMs) : NaN;
  return {
    skinId: typeof skin.id === 'string' ? skin.id : '',
    name: typeof skin.name === 'string' ? skin.name : '',
    rarity: RARITIES.includes(skin.rarity) ? skin.rarity : 'common',
    accentColor: HEX_COLOR.test(accent) ? accent : null,
    imageUrl: image ? resolveMediaUrl(image) : null,
    spinMs: Number.isFinite(spin) ? Math.min(MAX_SPIN_MS, Math.max(MIN_SPIN_MS, Math.round(spin))) : null,
  };
};

/** Whether a listener card has a now-playing card skin to preview. */
export const canPreviewCardSkin = (card) => Boolean(cardSkinLook(card?.wearing?.skin));

/**
 * Which song the card is showing, for ending a preview when it changes: the
 * playing queue entry, or null between songs and when nothing plays.
 */
export const playingSongKey = (nowPlaying) => {
  if (!nowPlaying) return null;
  const entry = nowPlaying.entryId ?? '';
  const song = nowPlaying.songId ?? '';
  return entry === '' && song === '' ? null : `${entry}:${song}`;
};

/**
 * A preview of `card`'s card skin, started while `songKey` plays; null when
 * the listener wears no now-playing card skin.
 */
export const startPreview = (card, songKey) => {
  const look = cardSkinLook(card?.wearing?.skin);
  if (!look) return null;
  return { userId: card.userId ?? null, name: card.displayName || '', look, songKey: songKey ?? null };
};

/** The preview still in force while `songKey` plays: null once the song has changed. */
export const previewForSong = (preview, songKey) => {
  if (!preview) return null;
  return preview.songKey === (songKey ?? null) ? preview : null;
};
